import type { ImageAsset } from '@rive-app/react-canvas';
import { decodeImage } from '@rive-app/react-canvas';
import type { TFunction } from 'i18next';

import type { BonusItem } from '../../../../dto/bonus.types';
import type { LoyaltyTierKey } from '../../../../stores/loyalty/types';
import { formatValueWithK } from '../../../../utils/helpers';
import { devError } from '../../../../utils/log';
import { setString, setNumber, setBool, setEnum } from '../../../../utils/riveHelper';
import { translateTierName } from '../../../../utils/tierTranslation';
import { formatTime } from '../../../../utils/time';

import { EMPTY_PRIZE, EMPTY_STOP } from './constants';
import type { PrizeData, ProgressStop } from './dailyBonusRive.types';

// ---------------------------------------------------------------------------
// Prize helpers
// ---------------------------------------------------------------------------

export function collectPrizes(item: BonusItem, isScModeAvailable: boolean, t: TFunction): PrizeData[] {
  const prizes: PrizeData[] = [];
  let prizeIndex = 1;

  if (item.gc_amount > 0) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'CC',
      placeholderNum: `day_${item.day}_prize_${prizeIndex}`,
      coinsAmount: formatValueWithK(item.gc_amount, t),
    });
    prizeIndex++;
  }

  if (item.sc_amount > 0 && isScModeAvailable) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'SC',
      placeholderNum: `day_${item.day}_prize_${prizeIndex}`,
      coinsAmount: formatValueWithK(item.sc_amount, t),
    });
    prizeIndex++;
  }

  if (item.gc_amount === 0 && item.sc_amount === 0 && item.image_url) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'MINI_GAME',
      placeholderNum: `day_${item.day}_prize_${prizeIndex}`,
      imageUrl: item.image_url,
    });
    prizeIndex++;
  }

  if (item.image_url_3) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'MINI_GAME',
      placeholderNum: `day_${item.day}_prize_${prizeIndex}`,
      imageUrl: item.image_url_3,
    });
  }

  return prizes;
}

function bindPrizeSlots(dayVM: any, prizes: PrizeData[], maxSlots: number) {
  for (let i = 0; i < maxSlots; i++) {
    const slotVM = dayVM?.viewModel?.(`prize${i + 1}`);
    if (!slotVM) continue;

    const prize = prizes[i] ?? EMPTY_PRIZE;
    setEnum(slotVM, 'prizeType', prize.prizeType);
    setString(slotVM, 'coinsAmount', prize.coinsAmount);
  }
}

// ---------------------------------------------------------------------------
// Day card binders
// ---------------------------------------------------------------------------

function bindDayCommon(
  dayVM: any,
  item: BonusItem,
  currentDay: number,
  maxSlots: number,
  isScModeAvailable: boolean,
  t: TFunction,
) {
  setString(dayVM, 'header', t('bonus.day', { day: item.day }));
  setBool(dayVM, 'isCurrentDay', currentDay === item.day);
  setBool(dayVM, 'isClaimed', currentDay >= item.day);

  const prizes = collectPrizes(item, isScModeAvailable, t);
  setNumber(dayVM, 'numOfPrizes', prizes.length);
  bindPrizeSlots(dayVM, prizes, maxSlots);
}

export function bindDayCards(
  popupVM: any,
  dailyBonuses: BonusItem[],
  currentDay: number,
  isScModeAvailable: boolean,
  t: TFunction,
) {
  if (!dailyBonuses.length) return;

  const lastDay = Math.max(...dailyBonuses.map((b) => b.day));

  for (const item of dailyBonuses) {
    const dayVM = popupVM?.viewModel?.(`day${item.day}`);
    if (!dayVM) continue;

    const maxSlots = item.day === lastDay ? 3 : 2;
    bindDayCommon(dayVM, item, currentDay, maxSlots, isScModeAvailable, t);
  }
}

// ---------------------------------------------------------------------------
// Top banner (monthly progress)
// ---------------------------------------------------------------------------

function buildProgressStops(monthlyBonusDay: number, milestoneDays: number[]): ProgressStop[] {
  return [
    { num: monthlyBonusDay, isCurrent: true, isChecked: false },
    ...milestoneDays.map((milestone) => ({
      num: milestone,
      isCurrent: monthlyBonusDay === milestone,
      isChecked: monthlyBonusDay >= milestone,
    })),
  ];
}

function ShowToolTipPrizes(item: BonusItem, isScModeAvailable: boolean, t: TFunction): PrizeData[] {
  const prizes: PrizeData[] = [];
  let prizeIndex = 1;

  if (item.gc_amount > 0) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'CC',
      placeholderNum: String(prizeIndex),
      coinsAmount: formatValueWithK(item.gc_amount, t),
    });
    prizeIndex++;
  }

  if (item.sc_amount > 0 && isScModeAvailable) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'SC',
      placeholderNum: String(prizeIndex),
      coinsAmount: formatValueWithK(item.sc_amount, t),
    });
    prizeIndex++;
  }

  if (item.gc_amount === 0 && item.sc_amount === 0 && item.image_url) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'MINI_GAME',
      placeholderNum: String(prizeIndex),
      imageUrl: item.image_url,
    });
    prizeIndex++;
  }

  return prizes;
}

export function bindMonthlyToolTips(
  rootVM: any,
  monthlyBonuses: BonusItem[],
  monthlyBonusDay: number,
  isScModeAvailable: boolean,
  t: TFunction,
) {
  const sorted = [...monthlyBonuses].sort((a, b) => a.day - b.day);

  for (let i = 0; i < sorted.length; i++) {
    const toolTipVM = rootVM?.viewModel?.(`toolTip${i + 1}`);
    if (!toolTipVM) continue;

    const item = sorted[i];
    const isClaimed = monthlyBonusDay >= item.day;

    const prizes = ShowToolTipPrizes(item, isScModeAvailable, t);
    setNumber(toolTipVM, 'numOfPrizes', prizes.length);

    for (let j = 0; j < 4; j++) {
      const prizeVM = toolTipVM?.viewModel?.(`prize${j + 1}`);
      if (!prizeVM) continue;

      const prize = prizes[j];
      if (prize) {
        setEnum(prizeVM, 'prizeType', prize.prizeType);
        setString(prizeVM, 'coinsAmount', prize.coinsAmount);
        setNumber(prizeVM, 'placeholderNum', Number(prize.placeholderNum));
      }
      setBool(prizeVM, 'prizeChecked', isClaimed);
    }
  }
}

function computeProgress(day: number, milestoneDays: number[]): number {
  if (day <= 0 || milestoneDays.length === 0) return 0;

  const stops = [0, ...milestoneDays];
  const sliceSize = 100 / milestoneDays.length;
  const lastStop = stops[stops.length - 1];
  if (day >= lastStop) return 100;

  for (let i = 1; i < stops.length; i++) {
    if (day === stops[i]) return i * sliceSize;
    if (day < stops[i]) {
      const segStart = stops[i - 1];
      const segEnd = stops[i];
      const segProgress = (day - segStart) / (segEnd - segStart);
      return (i - 1) * sliceSize + segProgress * sliceSize;
    }
  }
  return 0;
}

export function bindTopBanner(
  popupVM: any,
  isEntitledToBonus: boolean,
  monthlyBonusDay: number,
  monthlyBonuses: BonusItem[],
  tierKey: LoyaltyTierKey,
) {
  const topBannerVM = popupVM?.viewModel?.('topBanner');
  if (!topBannerVM) return;

  const milestoneDays = monthlyBonuses.map((b) => b.day).sort((a, b) => a - b);
  const progress = computeProgress(
    monthlyBonusDay,
    milestoneDays.map((d) => d - 1),
  );

  setNumber(topBannerVM, 'progBar', Math.round(progress));
  setString(topBannerVM, 'tier', translateTierName(tierKey));
  setBool(topBannerVM, 'isEntitledToBonus', isEntitledToBonus);

  const stops = buildProgressStops(monthlyBonusDay, milestoneDays);

  for (let i = 0; ; i++) {
    const progVM = topBannerVM?.viewModel?.(`prog${i + 1}`);
    if (!progVM) break;

    const stop = stops[i] ?? EMPTY_STOP;
    setNumber(progVM, 'num', stop.num);
    setBool(progVM, 'isCurrent', stop.isCurrent);
    setBool(progVM, 'isChecked', stop.isChecked);
  }
}

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------

export function bindCountdown(parentVM: any, timeUntilNextBonusMS: number) {
  const targetVM = parentVM;
  if (!targetVM) return;

  setString(targetVM, 'time', formatTime(timeUntilNextBonusMS, 'ms'));
}

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function loadRiveImage(url: string, asset: ImageAsset) {
  try {
    const res = await fetch(url);
    const image = await decodeImage(new Uint8Array(await res.arrayBuffer()));
    asset.setRenderImage(image);
    image.unref();
  } catch (e) {
    devError('Failed to load Rive asset image', e);
  }
}

// ---------------------------------------------------------------------------
// Referenced assets — maps placeholder names to image URLs for asset loading
// ---------------------------------------------------------------------------

export function buildReferencedAssets(
  dailyBonuses: BonusItem[],
  isScModeAvailable: boolean,
  t: TFunction,
): Record<string, string> {
  const assets: Record<string, string> = {};

  for (const item of dailyBonuses) {
    const prizes = collectPrizes(item, isScModeAvailable, t);
    for (const prize of prizes) {
      if (prize.imageUrl) {
        assets[prize.placeholderNum] = prize.imageUrl;
      }
    }
  }

  return assets;
}
